import {
  ActionError,
  AttentionBlock,
  Button,
  Checkbox,
  Chip,
  ChipAdd,
  ChoiceRow,
  ConfirmDialog,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  ListRow,
  FormFooter,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadingColumn,
  SavedIndicator,
  SearchInput,
  Skeleton,
  Surface,
  Textarea,
  formatRelative,
  toast,
} from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { CATEGORY_LABELS } from '@/features/procedures/labels'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import {
  useArchiveCatalogProcedure,
  useCatalogProcedure,
  useCatalogProcedures,
  useCatalogRequests,
  useResolveCatalogRequest,
  useSaveCatalogProcedure,
  useTemplateOptions,
  type CatalogProcedure,
  type CatalogProcedureInput,
  type TemplateOption,
  type TemplateState,
} from './api'
import { archiveEffect, fold, templateStateNote, usageLabel } from './labels'

/**
 * Criar e editar procedimento do catálogo (docs/areas/administracao.md, "Criar / editar
 * procedimento do catálogo"; RN-ADM-71, 73 e 74). Arquétipo: cadastro, de root. O item da lista
 * mestre diz o que o procedimento é e quais documentos o acompanham em toda clínica.
 */

const NAME_LIMIT = 80
const ALIAS_LIMIT = 12

interface LinkedTemplate {
  id: string
  name: string
  category: string
  state: TemplateState
  isRequired: boolean
}

interface FormState {
  name: string
  groupName: string
  description: string
  aliases: string[]
  /** O que está no campo "adicionar" e ainda não virou chip. */
  aliasDraft: string
  templates: LinkedTemplate[]
}

function emptyForm(name: string): FormState {
  return { name, groupName: '', description: '', aliases: [], aliasDraft: '', templates: [] }
}

function fromProcedure(procedure: CatalogProcedure): FormState {
  return {
    name: procedure.name,
    groupName: procedure.groupName,
    description: procedure.description ?? '',
    aliases: procedure.aliases,
    aliasDraft: '',
    templates: procedure.templates.map((template) => ({
      id: template.id,
      name: template.name,
      category: template.category,
      state: template.state,
      isRequired: template.isRequired,
    })),
  }
}

type ErrorKey = 'name' | 'groupName' | 'description' | 'aliases' | 'templates'
type Errors = Partial<Record<ErrorKey, string>>

function validate(form: FormState): Errors {
  const errors: Errors = {}
  const name = form.name.trim()
  if (name.length < 2) errors.name = 'Informe o nome do procedimento.'
  else if (name.length > NAME_LIMIT) errors.name = 'O nome pode ter no máximo 80 caracteres.'
  else if (name.includes(',')) {
    errors.name = 'Sem vírgulas: elas separam procedimentos no documento gerado.'
  }
  if (form.groupName.trim().length < 2) errors.groupName = 'Informe o grupo, como Injetáveis.'
  return errors
}

/** Por que o sinônimo não pode entrar na lista; `null` quando pode. */
function aliasProblem(text: string, aliases: string[]): string | null {
  if (aliases.length >= ALIAS_LIMIT) {
    return 'No máximo 12 sinônimos. Remova um para adicionar outro.'
  }
  if (text.length < 2 || text.length > 40) return 'Um sinônimo tem de 2 a 40 caracteres.'
  if (aliases.some((alias) => fold(alias) === fold(text))) return 'Esse sinônimo já está na lista.'
  return null
}

function toInput(form: FormState): CatalogProcedureInput {
  return {
    name: form.name.trim(),
    groupName: form.groupName.trim(),
    description: form.description.trim() || null,
    aliases: form.aliases,
    templates: form.templates.map((template) => ({
      templateId: template.id,
      isRequired: template.isRequired,
    })),
  }
}

/** Os erros por campo que a API devolve (422), na chave que o formulário usa. */
function serverErrors(error: unknown): Errors {
  const errors: Errors = {}
  for (const [key, message] of Object.entries(fieldErrors(error))) {
    if (key === 'name') errors.name = message
    else if (key === 'groupName') errors.groupName = message
    else if (key === 'description') errors.description = message
    else if (key.startsWith('aliases')) errors.aliases = message
    else if (key.startsWith('templates')) errors.templates = message
  }
  return errors
}

export function CatalogProcedurePage() {
  const { id } = useParams()
  const query = useCatalogProcedure(id)
  // O formulário nasce do item carregado; outro id, outro formulário.
  return <CatalogProcedureForm key={query.data?.id ?? 'new'} id={id} query={query} />
}

function CatalogProcedureForm({
  id,
  query,
}: {
  id: string | undefined
  query: ReturnType<typeof useCatalogProcedure>
}) {
  const isNew = !id
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Vindo de "criar e incluir": o nome pedido pela clínica e o pedido a atender depois de criar.
  const requestId = isNew ? params.get('request') : null
  const procedure = query.data
  const isArchived = Boolean(procedure?.archivedAt)

  const save = useSaveCatalogProcedure(id)
  const action = useArchiveCatalogProcedure()
  const resolve = useResolveCatalogRequest()
  // Os grupos que já existem, para a sugestão do campo GRUPO.
  const groups = useCatalogProcedures({ filter: 'active', perPage: 10 }).data?.meta.groups ?? []
  const groupsId = useId()

  const [form, setForm] = useState<FormState>(() =>
    procedure ? fromProcedure(procedure) : emptyForm(isNew ? (params.get('name') ?? '') : ''),
  )
  const [dirty, setDirty] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [touched, setTouched] = useState<{ name?: boolean; groupName?: boolean }>({})
  const [aliasError, setAliasError] = useState<string | null>(null)
  const [fromServer, setFromServer] = useState<Errors>({})
  const [picking, setPicking] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)

  function update(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }))
    setDirty(true)
    // O erro que veio da API vale para o valor enviado: mexeu no campo, ele sai.
    setFromServer((current) => {
      const next = { ...current }
      for (const key of Object.keys(patch)) delete next[key as ErrorKey]
      return next
    })
  }

  // O erro nasce ao sair do campo (ou ao enviar), nunca antes de a pessoa digitar.
  const found = validate(form)
  const errors: Errors = {
    name: (submitted || touched.name ? found.name : undefined) ?? fromServer.name,
    groupName:
      (submitted || touched.groupName ? found.groupName : undefined) ?? fromServer.groupName,
    description: fromServer.description,
    aliases: aliasError ?? fromServer.aliases,
    templates: fromServer.templates,
  }

  function addAlias() {
    const text = form.aliasDraft.trim()
    if (!text) return
    const problem = aliasProblem(text, form.aliases)
    if (problem) {
      setAliasError(problem)
      return
    }
    setAliasError(null)
    update({ aliases: [...form.aliases, text], aliasDraft: '' })
  }

  function toggleTemplate(option: TemplateOption, linked: boolean) {
    update({
      templates: linked
        ? [
            ...form.templates,
            {
              id: option.id,
              name: option.name,
              category: option.category,
              state: option.state,
              // O termo de consentimento nasce obrigatório; o termo avulso, sugerido.
              isRequired: option.category === 'informed_consent',
            },
          ]
        : form.templates.filter((template) => template.id !== option.id),
    })
  }

  function finish(message: string) {
    toast(message)
    navigate(routes.admin.catalogProcedures)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    setFailure(null)
    // O sinônimo digitado e ainda não adicionado entra junto: ninguém perde o que escreveu.
    let current = form
    const draft = form.aliasDraft.trim()
    if (draft) {
      const problem = aliasProblem(draft, form.aliases)
      if (problem) {
        setAliasError(problem)
        return
      }
      current = { ...form, aliases: [...form.aliases, draft], aliasDraft: '' }
      setForm(current)
    }
    if (Object.keys(validate(current)).length > 0) return

    save.mutate(toInput(current), {
      onSuccess: (saved) => {
        const name = current.name.trim()
        if (!isNew) return finish(`"${name}" salvo`)
        if (!requestId) return finish(`"${name}" entrou no catálogo`)
        resolve.mutate(
          { id: requestId, outcome: 'added', catalogProcedureId: saved.id },
          {
            onSuccess: (request) =>
              finish(`"${name}" entrou no catálogo — ${request.clinic.name} já pode adicionar`),
            onError: () =>
              finish(
                `"${name}" entrou no catálogo. O pedido da clínica continua aberto: responda em "incluir".`,
              ),
          },
        )
      },
      onError: (error) => setFromServer(serverErrors(error)),
    })
  }

  function runAction(kind: 'archive' | 'restore') {
    if (!procedure) return
    setFailure(null)
    action.mutate(
      { id: procedure.id, action: kind },
      {
        onSuccess: () => {
          if (kind === 'archive') {
            finish(`"${procedure.name}" arquivado — volta a qualquer momento`)
          } else toast(`"${procedure.name}" restaurado — volta à escolha das clínicas`)
        },
        onError: (error) => {
          setArchiving(false)
          setFailure({
            title:
              kind === 'archive'
                ? `Não foi possível arquivar "${procedure.name}"`
                : `Não foi possível restaurar "${procedure.name}"`,
            error,
          })
        },
      },
    )
  }

  const conflict =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as { id: string; isArchived: boolean } | undefined)
      : undefined
  const hasFieldError = save.isError && Object.keys(serverErrors(save.error)).length > 0
  const isReady = isNew || Boolean(procedure)
  // "Sem termo" é derivado: precisa de um termo de consentimento com versão publicada (RN-ADM-73).
  const hasConsentForm = form.templates.some(
    (template) => template.category === 'informed_consent' && template.state === 'published',
  )
  const linkedIds = new Set(form.templates.map((template) => template.id))

  return (
    <form onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Catálogo de procedimentos', href: routes.admin.catalogProcedures },
          { label: isNew ? 'novo' : (procedure?.name ?? '…') },
        ]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title={isNew ? 'Novo procedimento do catálogo' : (procedure?.name ?? 'Procedimento')}
            subtitle="Item da lista mestre da Westhetics: é dele que a clínica parte para cadastrar o que vende."
          />

          {!isNew && query.isError ? (
            <LoadError
              className="mt-6"
              title="Não conseguimos abrir este procedimento"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : !isNew && query.isPending ? (
            <div role="status" aria-label="Carregando" className="mt-6 flex flex-col gap-4">
              <Skeleton className="h-[340px] w-full" />
              <Skeleton className="h-[180px] w-full" />
              <Skeleton className="h-[96px] w-full" />
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {requestId ? <RequestContext requestId={requestId} /> : null}

              {isArchived && procedure ? (
                <AttentionBlock variant="lilac" title="Este procedimento está arquivado">
                  Ele não aparece na escolha das clínicas; os procedimentos que já o usam continuam
                  funcionando. Restaure para editar ou mudar os documentos.
                </AttentionBlock>
              ) : null}

              {save.isError && !hasFieldError ? (
                <ActionError
                  title="Não foi possível salvar o procedimento"
                  description={errorMessage(save.error)}
                  actions={
                    conflict ? (
                      <Button variant="outline" size="sm" asChild>
                        <Link to={routes.admin.catalogProcedure(conflict.id)}>
                          {conflict.isArchived ? 'Ver o arquivado' : 'Abrir o existente'}
                        </Link>
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              {failure ? (
                <ActionError
                  title={failure.title}
                  description={`${errorMessage(failure.error)} Nada mudou no catálogo.`}
                  onDismiss={() => setFailure(null)}
                />
              ) : null}

              <fieldset disabled={isArchived} className="flex min-w-0 flex-col gap-4">
                <FormBlock
                  title="Identificação"
                  note="É o nome que a clínica encontra na busca. A clínica pode usar outro no contrato."
                >
                  <div className="flex flex-col gap-4">
                    <Field
                      label="Nome"
                      meta={`${form.name.trim().length}/${NAME_LIMIT}`}
                      error={errors.name}
                      hint={
                        errors.name
                          ? undefined
                          : 'Sem vírgulas: elas separam procedimentos no documento gerado.'
                      }
                    >
                      <Input
                        value={form.name}
                        maxLength={120}
                        onChange={(event) => update({ name: event.target.value })}
                        onBlur={() => {
                          if (form.name.trim() !== '') setTouched({ ...touched, name: true })
                        }}
                      />
                    </Field>
                    <FieldGrid>
                      <Field
                        label="Grupo"
                        error={errors.groupName}
                        hint="Agrupa a lista de escolha da clínica. Use um grupo que já existe ou escreva um novo."
                      >
                        <Input
                          list={groupsId}
                          value={form.groupName}
                          maxLength={60}
                          autoComplete="off"
                          onChange={(event) => update({ groupName: event.target.value })}
                          onBlur={() => {
                            if (form.groupName.trim() !== '') {
                              setTouched({ ...touched, groupName: true })
                            }
                          }}
                        />
                        <datalist id={groupsId}>
                          {groups.map((name) => (
                            <option key={name} value={name} />
                          ))}
                        </datalist>
                      </Field>
                    </FieldGrid>
                    <Field
                      label="Sinônimos"
                      optional
                      meta={`${form.aliases.length}/${ALIAS_LIMIT}`}
                      error={errors.aliases}
                      hint="Outros nomes do mesmo procedimento, como botox. Servem à busca da clínica e ao vínculo do que veio do sistema antigo. Enter adiciona."
                    >
                      <div className="flex flex-col gap-2">
                        {form.aliases.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {form.aliases.map((alias) => (
                              <Chip
                                key={alias}
                                removeLabel="Remover sinônimo"
                                onRemove={() => {
                                  setAliasError(null)
                                  update({ aliases: form.aliases.filter((item) => item !== alias) })
                                }}
                              >
                                {alias}
                              </Chip>
                            ))}
                          </div>
                        ) : null}
                        <div className="flex items-stretch gap-2">
                          <Input
                            value={form.aliasDraft}
                            maxLength={40}
                            autoComplete="off"
                            onChange={(event) => {
                              setAliasError(null)
                              update({ aliasDraft: event.target.value })
                            }}
                            onKeyDown={(event) => {
                              // Enter adiciona o sinônimo; não envia o formulário.
                              if (event.key !== 'Enter') return
                              event.preventDefault()
                              addAlias()
                            }}
                          />
                          <ChipAdd className="shrink-0" onClick={addAlias}>
                            + Adicionar
                          </ChipAdd>
                        </div>
                      </div>
                    </Field>
                    <Field
                      label="Descrição"
                      optional
                      meta={`${form.description.length}/500`}
                      error={errors.description}
                      hint="Linha de apoio que a clínica lê ao escolher o procedimento."
                    >
                      <Textarea
                        value={form.description}
                        maxLength={500}
                        onChange={(event) => update({ description: event.target.value })}
                      />
                    </Field>
                  </div>
                </FormBlock>

                <FormBlock
                  title="Documentos que acompanham"
                  note="Entram em todo atendimento que inclui este procedimento, em qualquer clínica."
                >
                  <div className="flex flex-col gap-3">
                    {form.templates.length === 0 ? (
                      <p className="font-body text-body text-secondary font-light">
                        Nenhum documento vinculado ainda.
                      </p>
                    ) : (
                      <div
                        role="list"
                        aria-label="Documentos vinculados"
                        className="border-hairline border-y"
                      >
                        {form.templates.map((template) => (
                          <ListRow
                            key={template.id}
                            role="listitem"
                            className="max-tablet:flex-col max-tablet:items-stretch max-tablet:gap-2 px-0"
                            title={template.name}
                            meta={[
                              CATEGORY_LABELS[template.category] ?? template.category,
                              templateStateNote(template.state),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                            trailing={
                              <>
                                <RadioGroup
                                  aria-label={`Como "${template.name}" entra no atendimento`}
                                  className="flex gap-x-5"
                                  value={template.isRequired ? 'required' : 'suggested'}
                                  onValueChange={(value) =>
                                    update({
                                      templates: form.templates.map((item) =>
                                        item.id === template.id
                                          ? { ...item, isRequired: value === 'required' }
                                          : item,
                                      ),
                                    })
                                  }
                                >
                                  <label className={radioLabelClass}>
                                    <Radio value="required" />
                                    Obrigatório
                                  </label>
                                  <label className={radioLabelClass}>
                                    <Radio value="suggested" />
                                    Sugerido
                                  </label>
                                </RadioGroup>
                                <Button
                                  variant="text"
                                  size="sm"
                                  className="max-tablet:ml-auto"
                                  onClick={() =>
                                    update({
                                      templates: form.templates.filter(
                                        (item) => item.id !== template.id,
                                      ),
                                    })
                                  }
                                >
                                  remover
                                </Button>
                              </>
                            }
                          />
                        ))}
                      </div>
                    )}
                    {!hasConsentForm ? (
                      <p className="font-body text-body text-plum font-light">
                        Sem termo de consentimento publicado, o procedimento fica marcado "Sem
                        termo" no catálogo e na lista de cada clínica que o vende.
                      </p>
                    ) : null}
                    {errors.templates ? (
                      <p
                        role="alert"
                        className="font-body text-eyebrow text-plum leading-[1.5] font-light"
                      >
                        {errors.templates}
                      </p>
                    ) : null}
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-expanded={picking}
                        onClick={() => setPicking(!picking)}
                      >
                        {picking ? 'Fechar a busca' : '+ Vincular documento'}
                      </Button>
                    </div>
                    {picking ? (
                      <TemplatePicker linkedIds={linkedIds} onToggle={toggleTemplate} />
                    ) : null}
                  </div>
                </FormBlock>
              </fieldset>

              {procedure ? (
                <FormBlock title="Em uso">
                  <p className="border-hairline bg-pampas font-body text-body text-ink border px-4 py-3 font-light">
                    {procedure.usage.procedureCount === 0
                      ? 'Nenhuma clínica usa este procedimento ainda.'
                      : usageLabel(procedure.usage)}
                  </p>
                  <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
                    Renomear o item não muda o nome que cada clínica deu ao procedimento dela nem os
                    atendimentos já feitos.
                  </p>
                </FormBlock>
              ) : null}

              {isReady ? (
                <FormFooter
                  status={
                    procedure && !isArchived ? (
                      <SavedIndicator
                        savedAt={new Date(procedure.updatedAt)}
                        dirty={dirty}
                        saving={save.isPending}
                      />
                    ) : null
                  }
                  secondary={
                    <Button variant="outline" asChild>
                      <Link to={routes.admin.catalogProcedures}>Cancelar</Link>
                    </Button>
                  }
                  primary={
                    <Button
                      type="submit"
                      disabled={isArchived}
                      loading={save.isPending || resolve.isPending}
                      loadingLabel="Salvando…"
                    >
                      {isArchived ? 'Restaure para editar' : 'Salvar'}
                    </Button>
                  }
                />
              ) : null}

              {procedure ? (
                <Surface className="flex flex-wrap items-center justify-between gap-4">
                  <div className="max-w-[520px]">
                    <div className="font-display text-lead text-ink leading-[1.3] font-normal">
                      {isArchived ? 'Restaurar procedimento' : 'Arquivar procedimento'}
                    </div>
                    <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
                      {isArchived
                        ? 'Volta à lista mestre e à escolha das clínicas, com os mesmos documentos.'
                        : 'Sai da lista mestre e da escolha das clínicas. Quem já o usa continua funcionando, e ele volta a qualquer momento.'}
                    </p>
                  </div>
                  {isArchived ? (
                    <Button
                      variant="outline"
                      loading={action.isPending}
                      loadingLabel="Restaurando…"
                      onClick={() => runAction('restore')}
                    >
                      Restaurar
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => setArchiving(true)}>
                      Arquivar
                    </Button>
                  )}
                </Surface>
              ) : null}
            </div>
          )}
        </ReadingColumn>
      </Page>

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        tone="primary"
        title={`Arquivar "${procedure?.name ?? ''}"?`}
        effect={procedure ? archiveEffect(procedure.usage) : ''}
        confirmLabel="Arquivar"
        loading={action.isPending}
        loadingLabel="Arquivando…"
        onConfirm={() => runAction('archive')}
      />
    </form>
  )
}

const radioLabelClass =
  'font-body text-body text-ink min-h-touch-desk max-desk:min-h-touch flex cursor-pointer items-center gap-2 font-light'

/** De onde veio o cadastro: o pedido da clínica que ele atende ao ser salvo (RN-ADM-75). */
function RequestContext({ requestId }: { requestId: string }) {
  const requests = useCatalogRequests('open')
  const request = requests.data?.data.find((item) => item.id === requestId)
  return (
    <AttentionBlock variant="lilac" title="Este cadastro atende a um pedido de clínica">
      {request ? (
        <>
          {request.clinic.name} pediu "{request.name}" {formatRelative(new Date(request.createdAt))}
          . Ao salvar, o pedido é marcado como incluído e a clínica já pode adicionar o
          procedimento.
          {request.note ? (
            <span className="text-secondary"> A clínica escreveu: "{request.note}"</span>
          ) : null}
        </>
      ) : (
        'Ao salvar, o pedido é marcado como incluído e a clínica já pode adicionar o procedimento.'
      )}
    </AttentionBlock>
  )
}

/**
 * A busca de "Vincular documento": termos de consentimento e termos avulsos de Pacientes. A linha
 * inteira marca ou desmarca; o que está em rascunho pode entrar, mas ainda não conta como termo.
 */
function TemplatePicker({
  linkedIds,
  onToggle,
}: {
  linkedIds: Set<string>
  onToggle: (option: TemplateOption, linked: boolean) => void
}) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim())
  const query = useTemplateOptions(debouncedSearch)
  // Documento arquivado não acompanha procedimento (RN-ADM-73).
  const options = (query.data?.data ?? []).filter((option) => option.state !== 'archived')

  return (
    <div className="flex flex-col gap-3">
      <SearchInput
        autoFocus
        className="min-w-0"
        value={search}
        onChange={setSearch}
        placeholder="Buscar termo de consentimento ou termo avulso"
        aria-label="Buscar documento para vincular"
        onKeyDown={(event) => {
          // A busca fica dentro do formulário: Enter aqui não salva o procedimento.
          if (event.key === 'Enter') event.preventDefault()
        }}
      />
      {query.isError ? (
        <LoadError
          title="Não conseguimos buscar os documentos"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <div role="status" aria-label="Carregando documentos" className="flex flex-col gap-2">
          <Skeleton className="h-[48px] w-full" />
          <Skeleton className="h-[48px] w-full" />
          <Skeleton className="h-[48px] w-full" />
        </div>
      ) : options.length === 0 ? (
        <div className="border-hairline font-body text-body text-secondary flex flex-col items-start gap-3 border px-4 py-5 font-light">
          {debouncedSearch ? (
            <>
              <span>
                Nenhum documento para "{debouncedSearch}". Só termo de consentimento ou termo avulso
                de Pacientes acompanha um procedimento.
              </span>
              <Button variant="outline" size="sm" onClick={() => setSearch('')}>
                Limpar busca
              </Button>
            </>
          ) : (
            <>
              <span>
                Nenhum termo de consentimento ou termo avulso de Pacientes ainda. Crie o documento
                em Contratos e termos e volte para vincular.
              </span>
              <Button variant="outline" size="sm" asChild>
                <Link to={routes.admin.templates} target="_blank" rel="noreferrer">
                  Abrir Contratos e termos
                </Link>
              </Button>
            </>
          )}
        </div>
      ) : (
        <div
          role="group"
          aria-label="Documentos que podem acompanhar o procedimento"
          className="border-hairline max-h-[320px] overflow-y-auto border"
        >
          {options.map((option) => (
            <ChoiceRow
              key={option.id}
              bare
              control={
                <Checkbox
                  checked={linkedIds.has(option.id)}
                  onCheckedChange={(checked) => onToggle(option, checked === true)}
                  aria-label={option.name}
                />
              }
              description={[
                CATEGORY_LABELS[option.category] ?? option.category,
                option.currentVersion
                  ? `versão ${option.currentVersion.label}`
                  : templateStateNote(option.state),
              ]
                .filter(Boolean)
                .join(' · ')}
            >
              {option.name}
            </ChoiceRow>
          ))}
        </div>
      )}
    </div>
  )
}
